import { signal } from '@preact/signals-react'
import { getRandomId } from '@merels/game-logic'
import type { DeviceProfile, DeviceProfiles } from './deviceProfiles.types'

const PROFILES_KEY = 'merels_deviceProfiles'
const ASSIGNMENTS_KEY = 'merels_controllerProfileAssignment'

function readProfiles(): DeviceProfile[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(PROFILES_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeProfiles(profiles: DeviceProfile[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles))
  } catch {
    // Storage unavailable/full - profile list just won't persist this session.
  }
}

function readAssignments(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(ASSIGNMENTS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeAssignments(assignments: Record<string, string>): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(assignments))
  } catch {
    // Storage unavailable/full - assignment just won't persist this session.
  }
}

/**
 * Device profiles let a shared-screen controller "be" a named person, separate
 * from editing that person's name/thumbnail (see NameAndAvatarTab). See
 * ../../mateList/dialogs/ProfilePickerDialog for where profiles are picked/created.
 */
export function makeDeviceProfiles(): DeviceProfiles {
  const profiles = signal<DeviceProfile[]>(readProfiles())
  const assignments = readAssignments()
  const thumbnails = signal<Record<string, string>>({})
  const pickerRequestControllerId = signal<string | null>(null)

  function createProfile(name: string): DeviceProfile {
    const profile: DeviceProfile = { profileId: getRandomId(8), name }
    const next = [...profiles.value, profile]
    profiles.value = next
    writeProfiles(next)
    return profile
  }

  function renameProfile(profileId: string, name: string): void {
    const next = profiles.value.map((p) => (p.profileId === profileId ? { ...p, name } : p))
    profiles.value = next
    writeProfiles(next)
  }

  function getAssignedProfileId(controllerId: string): string | undefined {
    return assignments[controllerId]
  }

  function assignProfile(controllerId: string, profileId: string): void {
    assignments[controllerId] = profileId
    writeAssignments(assignments)
  }

  function setThumbnail(profileId: string, thumbnailUrl: string): void {
    thumbnails.value = { ...thumbnails.value, [profileId]: thumbnailUrl }
  }

  function requestPicker(controllerId: string): void {
    pickerRequestControllerId.value = controllerId
  }

  function clearPickerRequest(): void {
    pickerRequestControllerId.value = null
  }

  return {
    profiles,
    createProfile,
    renameProfile,
    getAssignedProfileId,
    assignProfile,
    thumbnails,
    setThumbnail,
    pickerRequestControllerId,
    requestPicker,
    clearPickerRequest,
  }
}
